import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useState } from "react";
import { TraitAssessmentResult } from "@dimensional-engineering/dimensional-models";

import elementsJsonData from "../../../assets/traitInfo/elements.json";
import patternsJsonData from "../../../assets/traitInfo/patterns.json";

import ModalWrapper from "@/components/shared/ModalWrapper/ModalWrapper";
import { TouchThroughWrapperTraits } from "@/components/assessments-new/views/AsessmentOutroScreen/TouchThroughWrapper/TouchThroughWrapper";
import { generateTraitAssessmentOutroSteps } from "./utils";
import {
  TraitAssessmentOutroComponents,
  TraitAssessmentOutroComponentsDelta,
  TraitAssessmentOutroContext,
  TraitAssessmentOutroNegativeAdapatations,
  TraitAssessmentOutroOrigins,
  TraitAssessmentOutroPositiveAdapatations,
  TraitAssessmentOutroScaleDefinition,
  TraitAssessmentOutroScaleReveal,
  TraitAssessmentOutroScoreChange,
  TraitAssessmentOutroScoreReveal,
  TraitAssessmentOutroTakeaways,
} from "./TraitAssessmentOutroSteps";
import { ExtendedPattern, ExtendedScale } from "@/models/sharedModels";
import {
  DeltaBreakdownTraitAssessmentElement,
  DeltaBreakdownTraitAssessmentPattern,
} from "@/components/assessments-new/views/AsessmentOutroScreen/DeltaBreakdown/DeltaBreakdown";
import { useModalContext } from "@/context/ModalContext";

type Props = {
  traitAssessmentResult: TraitAssessmentResult;
  type: "element" | "pattern";
  slug: string;
  isPremium?: boolean;
  source: "assessment completion" | "latest results item";
};

export default function TraitAssessmentOutro(props: Props) {
  const { setModalOpen } = useModalContext();

  const [step, setStep] = useState<number>(0);

  const isUserPremium = useSelector((state: RootState) => state.user.isPremium);
  const archetypeDiscoveries = useSelector(
    (state: RootState) => state.user.archetypesDiscovered
  );

  const stepMap = generateTraitAssessmentOutroSteps(
    props.type,
    isUserPremium,
    props.isPremium ?? false,
    archetypeDiscoveries
  );

  const element = elementsJsonData.find((e) => e.slug === props.slug) as
    | ExtendedScale
    | undefined;
  const pattern = patternsJsonData.find((p) => p.slug === props.slug) as
    | ExtendedPattern
    | undefined;

  const outroStepMap: Record<string, JSX.Element> = {
    scaleReveal: (
      <TraitAssessmentOutroScaleReveal
        traitAssessmentResult={props.traitAssessmentResult}
        isPremium={props.isPremium}
        slug={props.slug}
        type={props.type}
        element={element}
        pattern={pattern}
      />
    ),
    scoreDefinition: (
      <TraitAssessmentOutroScaleDefinition
        traitAssessmentResult={props.traitAssessmentResult}
        isPremium={props.isPremium}
        slug={props.slug}
        type={props.type}
        element={element}
        pattern={pattern}
      />
    ),
    scoreReveal: (
      <TraitAssessmentOutroScoreReveal
        traitAssessmentResult={props.traitAssessmentResult}
        isPremium={props.isPremium}
        slug={props.slug}
        type={props.type}
        element={element}
        pattern={pattern}
      />
    ),
    scoreChange: (
      <TraitAssessmentOutroScoreChange
        traitAssessmentResult={props.traitAssessmentResult}
        isPremium={props.isPremium}
        slug={props.slug}
        type={props.type}
        element={element}
        pattern={pattern}
      />
    ),
    components: (
      <TraitAssessmentOutroComponents
        traitAssessmentResult={props.traitAssessmentResult}
        isPremium={props.isPremium}
        slug={props.slug}
        type={props.type}
        element={element}
        pattern={pattern}
      />
    ),
    componentsChange: (
      <TraitAssessmentOutroComponentsDelta
        traitAssessmentResult={props.traitAssessmentResult}
        isPremium={props.isPremium}
        slug={props.slug}
        type={props.type}
        element={element}
        pattern={pattern}
      />
    ),
    context: (
      <TraitAssessmentOutroContext
        traitAssessmentResult={props.traitAssessmentResult}
        isPremium={props.isPremium}
        slug={props.slug}
        type={props.type}
        element={element}
        pattern={pattern}
      />
    ),
    positiveAdaptations: (
      <TraitAssessmentOutroPositiveAdapatations
        traitAssessmentResult={props.traitAssessmentResult}
        isPremium={props.isPremium}
        slug={props.slug}
        type={props.type}
        element={element}
        pattern={pattern}
      />
    ),
    negativeAdaptations: (
      <TraitAssessmentOutroNegativeAdapatations
        traitAssessmentResult={props.traitAssessmentResult}
        isPremium={props.isPremium}
        slug={props.slug}
        type={props.type}
        element={element}
        pattern={pattern}
      />
    ),
    origins: (
      <TraitAssessmentOutroOrigins
        traitAssessmentResult={props.traitAssessmentResult}
        isPremium={props.isPremium}
        slug={props.slug}
        type={props.type}
        element={element}
        pattern={pattern}
      />
    ),
    takeaway: (
      <TraitAssessmentOutroTakeaways
        traitAssessmentResult={props.traitAssessmentResult}
        isPremium={props.isPremium}
        slug={props.slug}
        type={props.type}
        element={element}
        pattern={pattern}
      />
    ),
  };

  const current = stepMap[step];

  if (step > Object.keys(stepMap).length - 1) {
    if (props.type === "pattern") {
      return (
        <ModalWrapper title="" noHeader noLine>
          <DeltaBreakdownTraitAssessmentPattern
            onFinish={() => setModalOpen(false)}
            onBack={() => setStep((current) => current - 1)}
            slug={props.slug}
            pattern={pattern}
            source={props.source}
          />
        </ModalWrapper>
      );
    } else {
      return (
        <ModalWrapper title="" noHeader noLine>
          <DeltaBreakdownTraitAssessmentElement
            onFinish={() => setModalOpen(false)}
            onBack={() => setStep((current) => current - 1)}
            slug={props.slug}
            element={element}
            source={props.source}
          />
        </ModalWrapper>
      );
    }
  }

  return (
    <ModalWrapper title="" noHeader noLine>
      <TouchThroughWrapperTraits
        type={props.type}
        slug={props.slug}
        numberOfSteps={Object.keys(stepMap).length}
        currentStep={step}
        onNext={() => {
          try {
            if (window.Android?.onHaptic) {
              window.Android.onHaptic();
            }
          } catch (error) {}

          setStep((current) => current + 1);
        }}
        onPrevious={() => {
          if (step === 0) {
            return;
          }
          try {
            if (window.Android?.onHaptic) {
              window.Android.onHaptic();
            }
          } catch (error) {}
          setStep((current) => current - 1);
        }}
      >
        {outroStepMap[current]}
      </TouchThroughWrapperTraits>
    </ModalWrapper>
  );
}
